import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { locations } from '../data/locations.ts';
import { categories, getProductsByCategory } from '../data/menuData.ts';
import { SHOW_MENU_PRICES } from '../config/ui.ts';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FloatingButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #25D366;
  border: none;
  color: var(--white);
  font-size: 1.8rem;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${props => !props.isOpen ? pulse : 'none'} 2s infinite;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ChatWindow = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 380px;
  height: 600px;
  background: var(--white);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 999;
  
  @media (max-width: 768px) {
    width: calc(100vw - 30px);
    height: 500px;
    right: 15px;
    left: 15px;
    bottom: 80px;
  }
`;

const ChatHeader = styled.div`
  background: #25D366;
  color: var(--white);
  padding: 20px;
  border-radius: 20px 20px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
  }
  
  span {
    font-size: 0.8rem;
    opacity: 0.9;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: rgba(227, 227, 221, 0.65);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Message = styled.div<{ isBot: boolean }>`
  max-width: 80%;
  align-self: ${props => props.isBot ? 'flex-start' : 'flex-end'};
  animation: ${fadeIn} 0.3s ease-out;
`;

const MessageBubble = styled.div<{ isBot: boolean }>`
  padding: 12px 16px;
  border-radius: ${props => props.isBot ? '5px 18px 18px 18px' : '18px 5px 18px 18px'};
  background: ${props => props.isBot ? 'var(--white)' : '#25D366'};
  color: ${props => props.isBot ? 'var(--navy-blue)' : 'var(--white)'};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  p {
    margin: 0 0 8px 0;
    line-height: 1.4;
    &:last-child {
      margin: 0;
    }
  }
`;

const QuickReplies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const QuickReplyButton = styled.button`
  background: var(--white);
  border: 2px solid #25D366;
  color: #25D366;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #25D366;
    color: var(--white);
  }
`;

const ProductCard = styled.div`
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px;
  margin: 5px 0;
  
  .name {
    font-weight: 600;
    color: #333;
  }
  
  .price {
    color: #25D366;
    font-weight: 600;
  }
  
  .description {
    font-size: 0.85rem;
    color: #666;
    margin-top: 4px;
  }
`;

const InputContainer = styled.div`
  padding: 15px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 10px;
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 25px;
  padding: 10px 15px;
  font-size: 0.9rem;
  outline: none;
  
  &:focus {
    border-color: #25D366;
  }
`;

const SendButton = styled.button`
  background: #25D366;
  color: var(--white);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #128C7E;
  }
`;

interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  quickReplies?: string[];
  products?: any[];
}

const WhatsAppBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        addBotMessage(
          '¡Hola! 👋 Soy el asistente de Pepe Tiburón. ¿En qué puedo ayudarte hoy?',
          ['📍 Ver sedes', '🍽️ Ver carta y precios', '📞 Contacto', '🚚 Delivery']
        );
      }, 300);
    }
  }, [isOpen, messages.length]);

  const addBotMessage = (text: string, quickReplies?: string[], products?: any[]) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      text,
      isBot: true,
      quickReplies,
      products
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addUserMessage = (text: string) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString() + '-user',
      text,
      isBot: false
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleQuickReply = (reply: string) => {
    addUserMessage(reply);
    processUserInput(reply);
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    addUserMessage(inputValue);
    processUserInput(inputValue);
    setInputValue('');
  };

  const processUserInput = (input: string) => {
    const lowerInput = input.toLowerCase();

    setTimeout(() => {
      // Respuestas sobre sedes
      if (lowerInput.includes('sede') || lowerInput.includes('local') || lowerInput.includes('📍')) {
        const sedesInfo = locations.map(loc => 
          `📍 *${loc.name}*\n📞 ${loc.phone}\n⏰ ${loc.hours}\n📍 ${loc.address}`
        ).join('\n\n');
        
        addBotMessage(
          `Tenemos ${locations.length} sedes en Arequipa:\n\n${sedesInfo}`,
          ['🍽️ Ver carta', '💬 Hablar con alguien', '🏠 Menú principal']
        );
      }
      // Respuestas sobre carta y precios
      else if (lowerInput.includes('carta') || lowerInput.includes('precio') || lowerInput.includes('menu') || lowerInput.includes('🍽️')) {
        addBotMessage(
          '¿Qué categoría te interesa ver?',
          categories.map(cat => cat.name)
        );
      }
      // Respuestas sobre categorías específicas
      else if (categories.some(cat => lowerInput.includes(cat.name.toLowerCase()))) {
        const category = categories.find(cat => lowerInput.includes(cat.name.toLowerCase()));
        if (category) {
          const products = getProductsByCategory(category.id).slice(0, 5);
          addBotMessage(
            `Aquí están algunos de nuestros ${category.name}:`,
            ['Ver más productos', '🍽️ Otra categoría', '💬 Hablar con alguien'],
            products
          );
        }
      }
      // Respuestas sobre delivery
      else if (lowerInput.includes('delivery') || lowerInput.includes('🚚')) {
        addBotMessage(
          '🚚 ¡Sí tenemos delivery! 🛵\n\nTodas nuestras sedes ofrecen servicio de delivery. Para hacer tu pedido:\n\n1. Elige tu sede más cercana\n2. Llámanos directamente\n3. O escríbenos por WhatsApp\n\nTiempo estimado: 30-45 minutos\n¿Desde qué sede te gustaría pedir?',
          locations.map(loc => loc.name)
        );
      }
      // Respuestas sobre contacto
      else if (lowerInput.includes('contacto') || lowerInput.includes('📞') || lowerInput.includes('telefono')) {
        addBotMessage(
          '📞 Puedes contactarnos de varias formas:\n\n1. Llamando a cualquiera de nuestras sedes\n2. Visitándonos personalmente\n3. Por WhatsApp\n\n¿Con qué sede te gustaría comunicarte?',
          [...locations.map(loc => loc.name), '💬 Hablar con alguien']
        );
      }
      // Selección de sede específica
      else if (locations.some(loc => lowerInput.includes(loc.name.toLowerCase()))) {
        const location = locations.find(loc => lowerInput.includes(loc.name.toLowerCase()));
        if (location) {
          addBotMessage(
            `Excelente elección! ${location.name}:\n\n📍 ${location.address}\n📞 ${location.phone}\n⏰ ${location.hours}\n\n¿Deseas contactar directamente con esta sede?`,
            ['💬 Sí, contactar por WhatsApp', '📍 Ver otras sedes', '🏠 Menú principal']
          );
        }
      }
      // Hablar con alguien o contactar por WhatsApp
      else if (lowerInput.includes('hablar') || lowerInput.includes('contactar') || lowerInput.includes('💬')) {
        addBotMessage(
          'Te voy a conectar con nuestro equipo. ¿Desde qué sede prefieres que te contacten?',
          locations.map(loc => loc.name)
        );
      }
      // Menú principal
      else if (lowerInput.includes('menu principal') || lowerInput.includes('🏠') || lowerInput.includes('inicio')) {
        addBotMessage(
          '¿En qué puedo ayudarte?',
          ['📍 Ver sedes', '🍽️ Ver carta y precios', '📞 Contacto', '🚚 Delivery']
        );
      }
      // Ver más productos
      else if (lowerInput.includes('ver más productos')) {
        addBotMessage(
          'Para ver nuestra carta completa, te recomiendo:\n\n1. Visitar la sección "Nuestra Carta" en la página\n2. O contactar directamente con una sede\n\n¿Qué prefieres hacer?',
          ['💬 Contactar con una sede', '🍽️ Ver otra categoría', '🏠 Menú principal']
        );
      }
      // Respuesta por defecto
      else {
        addBotMessage(
          'No estoy seguro de entender tu pregunta. ¿Puedo ayudarte con algo de esto?',
          ['📍 Ver sedes', '🍽️ Ver carta y precios', '📞 Contacto', '🚚 Delivery']
        );
      }
    }, 500);
  };

  const openWhatsApp = (locationName: string) => {
    const location = locations.find(loc => loc.name === locationName);
    if (location) {
      const message = `Hola! Vengo del chatbot de la web. Me gustaría hacer algunas consultas sobre ${location.name}.`;
      const phone = String(location.phone || '').replace(/\D/g, '').replace(/^51/, '');
      const whatsappUrl = `https://wa.me/51${phone}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      setIsOpen(false);
    }
  };

  return (
    <>
      <FloatingButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <i className={isOpen ? 'fas fa-times' : 'fab fa-whatsapp'} />
      </FloatingButton>

      <ChatWindow isOpen={isOpen}>
        <ChatHeader>
          <ChatTitle>
            <i className="fab fa-whatsapp" />
            <div>
              <h3>Pepe Tiburón</h3>
              <span>Asistente Virtual</span>
            </div>
          </ChatTitle>
          <CloseButton onClick={() => setIsOpen(false)}>
            <i className="fas fa-times" />
          </CloseButton>
        </ChatHeader>

        <MessagesContainer>
          {messages.map(message => (
            <Message key={message.id} isBot={message.isBot}>
              <MessageBubble isBot={message.isBot}>
                {message.text.split('\n').map((line, index) => (
                  <p key={index}>
                    {line.replace(/\*([^*]+)\*/g, '$1')}
                  </p>
                ))}
                
                {message.products && message.products.map(product => (
                  <ProductCard key={product.id}>
                    <div className="name">{product.name}</div>
                    {SHOW_MENU_PRICES && (
                      <div className="price">S/. {product.price.toFixed(2)}</div>
                    )}
                    {product.description && (
                      <div className="description">{product.description.substring(0, 60)}...</div>
                    )}
                  </ProductCard>
                ))}
              </MessageBubble>
              
              {message.quickReplies && (
                <QuickReplies>
                  {message.quickReplies.map((reply, index) => (
                    <QuickReplyButton 
                      key={index}
                      onClick={() => {
                        if (reply.includes('Sí, contactar por WhatsApp')) {
                          const lastLocationMessage = messages.slice().reverse().find(m => 
                            m.text.includes('Excelente elección!')
                          );
                          if (lastLocationMessage) {
                            const locationName = lastLocationMessage.text.match(/Excelente elección! (.+?):/)?.[1];
                            if (locationName) openWhatsApp(locationName);
                          }
                        } else if (locations.some(loc => loc.name === reply)) {
                          handleQuickReply(reply);
                          if (messages[messages.length - 1].text.includes('Te voy a conectar')) {
                            setTimeout(() => openWhatsApp(reply), 1000);
                          }
                        } else {
                          handleQuickReply(reply);
                        }
                      }}
                    >
                      {reply}
                    </QuickReplyButton>
                  ))}
                </QuickReplies>
              )}
            </Message>
          ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <Input
            type="text"
            placeholder="Escribe tu mensaje..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          />
          <SendButton onClick={handleSend}>
            <i className="fas fa-paper-plane" />
          </SendButton>
        </InputContainer>
      </ChatWindow>
    </>
  );
};

export default WhatsAppBot;